"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES, VIDEO_FILE_TYPES, VIDEO_MIME_TYPE_ENUM } from "@/libs/builder/consts";
import { fullRichtextControls } from "@/types/richtext-controls";

export const heroDefaultValues = {
  backgroundType: "image",
  gradientPosition: "top",
  mobileVariant: "transparentContentOnTop",
  videoFileReference: "",
  videoFileType: "",
  tabletVideoFileReference: "",
  tabletVideoFileType: "",
  mobileVideoFileReference: "",
  mobileVideoFileType: "",
  displayScrollBar: false,
  logoReference: "",
  clipped: false,
  legalDisclaimer: "Legal Disclaimer",
  desktopImage: {
    src: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F8ffca99c7ff4421f904e63edc9935589",
    title: "desktopImage",
    alt: "desktopImage",
  },
  smallImage: {
    src: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F8ffca99c7ff4421f904e63edc9935589",
    title: "smallImage",
    alt: "smallImage",
  },
  content: {
    title: "Title",
    tagLine: "XPRESS YOUR UNIQUENESS",
    text: "Description",
    cta: [
      {
        additionalBehavior: "always",
        label: "Button",
        theme: "light",
        layout: "full",
        tracking: "tracking",
        link: "/",
        openIn: "new Tab",
        target: "",
      },
    ],
    discounts: [
      {
        currentPrice: "CHF 19.90",
        originalPrice: "CHF 29.90",
        packInformation: "Per pack",
        priceDisclaimer: "Offer available until 31/10/2023",
      },
    ],
  },
  theme: "dark",
};

export const heroInputs = [
  {
    name: "data",
    type: "object",
    required: true,
    subFields: [
      {
        name: "backgroundType",
        type: "string",
        enum: ["image", "video"],
        required: true,
        defaultValue: "image",
      },
      {
        name: "theme",
        type: "string",
        enum: ["light", "dark", "inherit"],
        defaultValue: "dark",
      },
      {
        name: "mobileVariant",
        type: "string",
        defaultValue: "transparentContentOnTop",
        enum: ["transparentContentOnTop", "contentAtTheBottomWithBackground"],
        required: true,
      },
      {
        name: "logo",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "logoAlt",
        type: "string",
        defaultValue: "Logo",
      },
      {
        name: "logoWidth",
        type: "number",
      },
      {
        name: "logoHeight",
        type: "number",
      },
      {
        name: "content",
        type: "object",
        defaultValue: {
          title: "Title",
        },
        required: true,
        subFields: [
          {
            name: "tagLine",
            type: "string",
          },
          {
            name: "title",
            type: "string",
          },
          {
            name: "titleLogged",
            type: "string",
          },
          {
            name: "subheadline",
            type: "string",
          },
          {
            name: "subheadlineLogged",
            type: "string",
          },
          {
            name: "text",
            type: "richTextEditor",
            options: {
              controls: fullRichtextControls,
            },
          },
          {
            name: "textLogged",
            type: "richTextEditor",
            options: {
              controls: fullRichtextControls,
            },
          },
          {
            name: "cta",
            type: "list",
            subFields: [
              {
                name: "additionalBehavior",
                type: "string",
                defaultValue: "always",
                required: true,
                enum: ["always", "onlyForLoggedIn", "onlyForNotLoggedIn"],
              },
              {
                name: "label",
                type: "string",
                required: true,
              },
              {
                name: "theme",
                type: "string",
                defaultValue: "light",
                enum: ["light", "dark"],
                required: true,
              },
              {
                name: "layout",
                type: "string",
                enum: ["chevron", "full", "border"],
                required: true,
              },
              {
                name: "tracking",
                type: "string",
                required: true,
              },
              {
                name: "link",
                type: "url",
                required: true,
              },
              {
                name: "openIn",
                type: "string",
                enum: ["new Tab", "same Tab"],
                friendlyName: "Open in",
                onChange: `options.set("target", (options.get("openIn") === "new Tab"? "_blank" : ""))`,
                defaultValue: "same Tab",
              },
              {
                hideFromUI: true,
                name: "target",
                type: "string",
                enum: ["", "_blank"],
                required: true,
                defaultValue: "",
              },
            ],
          },
          {
            name: "discounts",
            type: "list",
            subFields: [
              {
                name: "currentPrice",
                type: "string",
                required: true,
              },
              {
                name: "originalPrice",
                type: "string",
                required: true,
              },
              {
                name: "packInformation",
                type: "string",
              },
              {
                name: "priceDisclaimer",
                type: "string",
              },
            ],
          },
        ],
      },
      {
        name: "desktopImage",
        type: "object",
        showIf: `options.get("backgroundType") === "image"`,
        subFields: [
          {
            name: "src",
            type: "file",
            required: true,
            allowedFileTypes: IMAGE_FILE_TYPES,
          },
          {
            name: "title",
            type: "string",
            required: true,
          },
          {
            name: "alt",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "mediumImage",
        type: "object",
        showIf: `options.get("backgroundType") === "image"`,
        subFields: [
          {
            name: "src",
            type: "file",
            allowedFileTypes: IMAGE_FILE_TYPES,
          },
          {
            name: "title",
            type: "string",
          },
          {
            name: "alt",
            type: "string",
          },
        ],
      },
      {
        name: "smallImage",
        type: "object",
        showIf: `options.get("backgroundType") === "image"`,
        subFields: [
          {
            name: "src",
            type: "file",
            allowedFileTypes: IMAGE_FILE_TYPES,
            required: true,
          },
          {
            name: "title",
            type: "string",
            required: true,
          },
          {
            name: "alt",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "videoFileReference",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        showIf: `options.get("backgroundType") === "video"`,
      },
      {
        name: "videoFileType",
        type: "string",
        friendlyName: "Desktop video MIME type",
        enum: VIDEO_MIME_TYPE_ENUM,
        showIf: `options.get("backgroundType") === "video"`,
      },
      {
        name: "tabletVideoFileReference",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        showIf: `options.get("backgroundType") === "video"`,
      },
      {
        name: "tabletVideoFileType",
        type: "string",
        friendlyName: "Tablet video MIME type",
        enum: VIDEO_MIME_TYPE_ENUM,
        showIf: `options.get("backgroundType") === "video"`,
      },
      {
        name: "mobileVideoFileReference",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        showIf: `options.get("backgroundType") === "video"`,
      },
      {
        name: "mobileVideoFileType",
        type: "string",
        friendlyName: "Mobile video MIME type",
        enum: VIDEO_MIME_TYPE_ENUM,
        showIf: `options.get("backgroundType") === "video"`,
      },
      {
        name: "displayScrollBar",
        type: "boolean",
        defaultValue: false,
        required: true,
      },
      {
        name: "logoReference",
        type: "string",
      },
      {
        name: "clipped",
        type: "boolean",
        defaultValue: false,
        required: true,
      },
      {
        name: "legalDisclaimer",
        type: "string",
      },
      {
        name: "gradientPosition",
        friendlyName: "gradient position desktop",
        type: "string",
        enum: ["none", "left", "top"],
        defaultValue: "top",
      },
      {
        name: "gradientPositionTablet",
        friendlyName: "gradient position tablet",
        type: "string",
        enum: ["none", "left", "top"],
        defaultValue: "top",
      },
      {
        name: "gradientPositionMobile",
        friendlyName: "gradient position mobile",
        type: "string",
        enum: ["none", "left", "top"],
        defaultValue: "top",
      },
    ],
    defaultValue: heroDefaultValues,
  },
] as const satisfies Inputs;

export const HeroComponent: RegisteredComponent = {
  name: "Hero",
  component: dynamic(async () => await import("./Hero")),
  inputs: heroInputs,
};
